/**
 * Dashboard color themes (Settings → Appearance; design 2026-10-08, screens Oppih / vcwY7 / X1QwI).
 *
 * `penVariables.json` is the design file's color variables, copied 1:1 from the .pen with `GetVariables()`: every themed
 * variable has one value per theme on the `theme` axis (civic … custom), the rest are fixed. `scripts/build-theme-css.mjs`
 * turns it into styles/themes.css (civic on :root, one `[data-theme]` block per theme, plus the higher-contrast blocks).
 *
 * A custom theme is six colors from the editor (Background, Cards, Text, Accent, Chart 2, Chart 3). `deriveCustomTheme()`
 * builds every themed variable from them the way the designer built the "Navy & orange" sample: first the core colors
 * (app, surface, subtle, muted, inverse, ink, text-secondary, text-muted, faint, border, accent, chart-2, chart-3, link,
 * shadow), then every other variable as a fixed mix of those. DOM-free: the Worker imports it to inline a saved custom
 * theme before first paint.
 */
import pen from "./penVariables.json";

export const THEME_IDS = ["civic", "modern", "graphite", "lakefront", "prairie", "harbor", "rose", "custom"] as const;
export type ThemeId = (typeof THEME_IDS)[number];
export type PresetId = Exclude<ThemeId, "custom">;
export const PRESET_IDS = THEME_IDS.filter((t): t is PresetId => t !== "custom");
export const THEME_LABEL: Record<ThemeId, string> = {
  civic: "Civic", modern: "Modern", graphite: "Graphite", lakefront: "Lakefront", prairie: "Prairie", harbor: "Harbor", rose: "Rose", custom: "Custom",
};
/** "Match my device" uses this theme while the device is in dark mode. */
export const DARK_THEME: PresetId = "graphite";

/** Variable name → "#RRGGBB" or "#RRGGBBAA". */
export type Vars = Record<string, string>;

const themed = pen.themed as Record<string, string[]>;
/** Every variable that changes with the theme (the design's themed variables, in the design's order). */
export const THEMED_NAMES: readonly string[] = Object.keys(themed);
/** Fixed on purpose: brand-*, leaning-*, canvas-bg, colors over video, recording. */
export const FIXED_VARS: Readonly<Vars> = pen.fixed;

const col = (t: ThemeId) => (pen.order as string[]).indexOf(t);
export function presetVars(t: ThemeId): Vars {
  const i = col(t);
  return Object.fromEntries(THEMED_NAMES.map((k) => [k, `#${themed[k][i]}`]));
}

// ------------------------------------------------------------------ color math
export const isHex6 = (s: unknown): s is string => typeof s === "string" && /^#[0-9A-Fa-f]{6}$/.test(s);
const rgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const hex2 = (n: number) => Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, "0").toUpperCase();
const toHex = (c: number[]) => `#${c.map(hex2).join("")}`;
/** a → b by t (sRGB), like the designer's mixes. */
export function mix(a: string, b: string, t: number): string {
  const A = rgb(a), B = rgb(b);
  return toHex(A.map((x, i) => x + (B[i] - x) * t));
}
const withAlpha = (h: string, alpha: string) => `${h.slice(0, 7).toUpperCase()}${alpha}`;

/** WCAG 2 relative luminance. */
export function luminance(h: string): number {
  const [r, g, b] = rgb(h).map((v) => { const s = v / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
/** WCAG 2 contrast ratio, 1–21. */
export function contrastRatio(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)].sort((m, n) => n - m);
  return (x + 0.05) / (y + 0.05);
}

// ------------------------------------------------------------------ custom themes
export interface CustomTheme {
  name: string;
  /** The "Start from" preset last picked. */
  base: PresetId;
  background: string; cards: string; text: string; accent: string; chart2: string; chart3: string;
}
export const CUSTOM_KEYS = ["background", "cards", "text", "accent", "chart2", "chart3"] as const;
export type CustomKey = (typeof CUSTOM_KEYS)[number];

/** The editor's six colors of a preset ("Start from"). */
export function colorsOf(t: ThemeId): Pick<CustomTheme, CustomKey> {
  const v = presetVars(t);
  return { background: v["bg-app"], cards: v["bg-surface"], text: v["text-primary"], accent: v.accent, chart2: v["chart-2"], chart3: v["chart-3"] };
}
/** The design's sample custom theme. */
export const SAMPLE_CUSTOM: CustomTheme = { name: "Navy & orange", base: "harbor", ...colorsOf("custom") };

/** Same in every light theme / in the dark one (status colors, highlights, topic chips). */
const CONSTANT = ["positive", "positive-bg", "warning", "warning-bg", "negative", "negative-bg", "live", "live-bg", "live-border", "live-ring",
  "type-topic", "type-topic-bg", "highlight", "notes-bg", "highlight-yellow-strong", "highlight-green", "highlight-green-soft"] as const;
const ACCENT_ALPHA = { 2: "05", 4: "0A", 5: "0D", 6: "10", 8: "14", 12: "1F", 15: "26", 20: "33", 25: "40", 35: "59", 50: "80" } as const;
const SHADOWS = [6, 8, 10, 12, 14, 15, 16, 20, 22, 25, 35] as const;
const OVERLAY_ALPHA = { "overlay-strong": "C7", overlay: "B3", "overlay-soft": "A6" } as const;
/** Neutrals: background → text by t (fitted on the design's light themes). */
const NEUTRAL: Record<string, number> = {
  "bg-subtle": 0.018, "bg-muted": 0.048, "border-divider": 0.053, "chart-grid": 0.053, "fill-track": 0.072, "border-card": 0.083,
  "type-committee-bg": 0.04, "forecast-tossup-bg": 0.04, "border-control": 0.143, "chapter-procedure": 0.151, "chart-muted": 0.206,
  "border-strong": 0.233, "text-disabled": 0.243, "forecast-tossup": 0.282, "text-faint": 0.344, "mark-committee": 0.344,
  "icon-muted": 0.463, "text-muted": 0.582, "text-nav": 0.683, "text-secondary": 0.786, "type-committee": 0.786, "text-strong": 0.903,
};
/** Tints: cards → accent by t. */
const ACCENT_TINT: Record<string, number> = {
  "speaking-line": 0.1, "type-legislation-bg": 0.11, "forecast-for-bg": 0.11, "avatar-bg": 0.12, selection: 0.16, "chapter-current": 0.2,
  "highlight-blue": 0.22, "seq-1": 0.25, "seq-2": 0.5, "seq-3": 0.78,
};

export const isDarkTheme = (c: Pick<CustomTheme, "background" | "text">) => luminance(c.background) < luminance(c.text);

/** Every themed variable for a custom theme. */
export function deriveCustomTheme(c: Pick<CustomTheme, CustomKey>): Vars {
  const dark = isDarkTheme(c);
  const ref = presetVars(dark ? "graphite" : "custom");
  const bg = c.background.toUpperCase(), surface = c.cards.toUpperCase(), text = c.text.toUpperCase(), accent = c.accent.toUpperCase();
  const chart2 = c.chart2.toUpperCase(), chart3 = c.chart3.toUpperCase();
  const shade = dark ? "#000000" : text;   // overlays and shadows
  // Core colors.
  const v: Vars = {
    "bg-app": bg, "bg-surface": surface, "bg-inverse": text, "text-primary": text, "text-inverse": dark ? bg : surface,
    accent, "chart-1": accent, "chart-2": chart2, "chart-3": chart3,
    link: dark ? mix(accent, text, 0.15) : mix(accent, "#000000", 0.11),
  };
  for (const [k, t] of Object.entries(NEUTRAL)) v[k] = mix(bg, text, t);
  // From the core colors.
  for (const [k, t] of Object.entries(ACCENT_TINT)) v[k] = mix(surface, accent, t);
  Object.assign(v, {
    "type-legislation": accent, "forecast-for": accent, "forecast-against": chart2, "forecast-against-bg": mix(surface, chart2, 0.11),
    "seq-4": mix(accent, text, 0.3), "text-avatar": mix(accent, text, 0.55),
    "type-person": mix(chart3, text, 0.15), ai: mix(chart3, text, 0.15), "type-person-bg": mix(surface, chart3, 0.11), "ai-bg": mix(surface, chart3, 0.11),
    "bg-app-clear": withAlpha(bg, "00"),
  });
  for (const k of CONSTANT) v[k] = ref[k];
  for (const [k, a] of Object.entries(OVERLAY_ALPHA)) v[k] = withAlpha(shade, a);
  for (const [n, a] of Object.entries(ACCENT_ALPHA)) v[`accent-${n}`] = withAlpha(accent, a);
  for (const n of SHADOWS) v[`shadow-${n}`] = withAlpha(shade, ref[`shadow-${n}`].slice(7));
  return v;
}

// ------------------------------------------------------------------ "Higher contrast text"
/** Darkens secondary text and borders toward the text color, in any theme. */
export function highContrastVars(v: Vars): Vars {
  const ink = v["text-primary"];
  const toInk = (k: string, t: number) => mix(v[k], ink, t);
  return {
    "text-strong": toInk("text-strong", 0.5), "text-secondary": toInk("text-secondary", 0.55), "type-committee": toInk("type-committee", 0.55),
    "text-nav": toInk("text-nav", 0.55), "text-muted": toInk("text-muted", 0.5), "text-faint": toInk("text-faint", 0.4), "icon-muted": toInk("icon-muted", 0.45),
    "border-divider": toInk("border-divider", 0.1), "border-card": toInk("border-card", 0.14), "border-control": toInk("border-control", 0.2),
    "border-strong": toInk("border-strong", 0.25),
  };
}

// ------------------------------------------------------------------ Readability (custom theme editor)
export interface ReadabilityRow { id: "text" | "accent" | "chart3"; label: string; ratio: number; ok: boolean; text: string }
/** WCAG AA for text is 4.5:1; below it the color is still fine for bars and fills ("Bars only"). */
export function readability(c: Pick<CustomTheme, CustomKey>): ReadabilityRow[] {
  const row = (id: ReadabilityRow["id"], label: string, fg: string, bg: string): ReadabilityRow => {
    const ratio = contrastRatio(fg, bg), ok = ratio >= 4.5;
    return { id, label, ratio, ok, text: `${ratio.toFixed(1)}:1 · ${ok ? "Passes" : "Bars only"}` };
  };
  return [row("text", "Text on background", c.text, c.background), row("accent", "Accent on cards", c.accent, c.cards), row("chart3", "Chart 3 on cards", c.chart3, c.cards)];
}

// ------------------------------------------------------------------ CSS
export const cssDecls = (v: Vars) => Object.entries(v).map(([k, x]) => `--${k}: ${x};`).join(" ");
/** The <style> body for a saved custom theme (its plain and higher-contrast variables). */
export function customThemeCss(c: Pick<CustomTheme, CustomKey>): string {
  const v = deriveCustomTheme(c);
  return `:root[data-theme="custom"] { ${isDarkTheme(c) ? "color-scheme: dark; " : ""}${cssDecls(v)} }\n`
    + `:root[data-theme="custom"][data-contrast="high"] { ${cssDecls(highContrastVars(v))} }`;
}

/** styles/themes.css (scripts/build-theme-css.ts writes it; tests/appearance.test.ts checks it is current). */
export function themesCss(): string {
  // Civic on plain :root; a [data-theme] block is more specific, so it wins. The civic contrast block must not match other themes.
  const sel = (t: ThemeId, extra = "") => (t !== "civic" ? `:root[data-theme="${t}"]${extra}`
    : extra ? `:root:not([data-theme])${extra}, :root[data-theme="civic"]${extra}` : `:root, :root[data-theme="civic"]`);
  const block = (s: string, v: Vars, pre = "") => `${s} {\n${pre}${Object.entries(v).map(([k, x]) => `  --${k}: ${x};\n`).join("")}}\n`;
  const out = ["/* Generated by scripts/build-theme-css.ts from dashboard-src/theme/penVariables.json (the design's variables). Don't edit by hand.\n"
    + "   One block per theme on <html data-theme>; civic is the default. data-contrast=\"high\" is Settings → Appearance → Higher contrast text. */\n"];
  for (const t of THEME_IDS) out.push(block(sel(t), presetVars(t), t === DARK_THEME ? "  color-scheme: dark;\n" : ""));
  for (const t of THEME_IDS) out.push(block(sel(t, '[data-contrast="high"]'), highContrastVars(presetVars(t))));
  return out.join("\n");
}

/** A saved custom theme from untrusted input, or null. */
export function cleanCustomTheme(x: unknown): CustomTheme | null {
  const o = (x && typeof x === "object" ? x : {}) as Record<string, unknown>;
  if (!CUSTOM_KEYS.every((k) => isHex6(o[k]))) return null;
  const name = typeof o.name === "string" ? o.name.replace(/[\u0000-\u001f]/g, "").trim().slice(0, 60) : "";
  const base = PRESET_IDS.includes(o.base as PresetId) ? (o.base as PresetId) : "civic";
  return { name, base, ...Object.fromEntries(CUSTOM_KEYS.map((k) => [k, String(o[k]).toUpperCase()])) as Pick<CustomTheme, CustomKey> };
}
